import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { signOut } from 'app/store/authSlice';
import { userService } from 'app/services/user.service';
import type {
  SSOConfiguration,
  SSOMetadataValidationResponse,
  SSOOperationResponse,
  VerifySSODomainResponse,
} from 'graphql_custom/sso/types';
import type { RootState } from './rootReducer';

export interface SSOConfigState {
  configuration: SSOConfiguration | null;
  loading: boolean;
  operationLoading: boolean;
  /** Per-domain verification result, keyed by domain. Lives in store so it
   * survives re-renders and the wizard can show inline DNS diagnostics. */
  verificationByDomain: Record<string, VerifySSODomainResponse>;
  /** Last validated metadata URL result, cached so refreshes of step 4 stay useful. */
  metadataValidation: SSOMetadataValidationResponse | null;
  /** Existing users freshly linked during the most recent activation. */
  lastNewlyLinkedUserCount: number | null;
  /** Existing users that were already linked to the provider during the most
   * recent activation (no-op, but sign-in still works for them). */
  lastAlreadyLinkedUserCount: number | null;
  error: string | null;
}

const initialState: SSOConfigState = {
  configuration: null,
  loading: false,
  operationLoading: false,
  verificationByDomain: {},
  metadataValidation: null,
  lastNewlyLinkedUserCount: null,
  lastAlreadyLinkedUserCount: null,
  error: null,
};

export const getSSOSetupThunk = createAsyncThunk('ssoConfig/getSSOSetup', async (paidolId: string) =>
  userService.getSSOSetup(paidolId)
);

export const initiateSSOSetupThunk = createAsyncThunk(
  'ssoConfig/initiateSSOSetup',
  async (input: { paidolId: string; domains: string[] }) => userService.initiateSSOSetup(input)
);

export const addSSODomainThunk = createAsyncThunk(
  'ssoConfig/addSSODomain',
  async (input: { paidolId: string; domain: string }) => userService.addSSODomain(input)
);

export const removeSSODomainThunk = createAsyncThunk(
  'ssoConfig/removeSSODomain',
  async (input: { paidolId: string; domain: string }) => userService.removeSSODomain(input)
);

export const verifySSODomainThunk = createAsyncThunk(
  'ssoConfig/verifySSODomain',
  async (input: { paidolId: string; domain: string }) => {
    const response = await userService.verifySSODomain(input);
    return { domain: input.domain, response };
  }
);

export const validateSSOMetadataThunk = createAsyncThunk(
  'ssoConfig/validateSSOMetadata',
  async (input: { paidolId: string; metadataUrl: string }) => userService.validateSSOMetadata(input)
);

export const saveSSOProviderThunk = createAsyncThunk(
  'ssoConfig/saveSSOProvider',
  async (input: { paidolId: string; metadataUrl: string }) => userService.saveSSOProvider(input)
);

export const activateSSOThunk = createAsyncThunk(
  'ssoConfig/activateSSO',
  async (input: {
    paidolId: string;
    domains?: { domain: string; verificationToken: string }[];
    metadataUrl?: string;
  }) => userService.activateSSO(input)
);

export const deactivateSSOThunk = createAsyncThunk(
  'ssoConfig/deactivateSSO',
  async (input: { paidolId: string }) => userService.deactivateSSO(input)
);

export const relinkSSOUsersThunk = createAsyncThunk(
  'ssoConfig/relinkSSOUsers',
  async (input: { paidolId: string }) => userService.relinkSSOUsers(input)
);

/** Polls getSSOSetup every 3s while linkingStatus is IN_PROGRESS so the
 * SSOStatusView can render a live progress card. Stops on any other status
 * or when the polling thunk is cancelled (e.g. component unmount).
 *
 * Dispatches `applyPolledSSOConfiguration` (a plain reducer, not the
 * loading-toggling getSSOSetupThunk) so the page doesn't flash a spinner
 * every poll cycle and SSOStatusView doesn't unmount-remount. */
export const pollSSOLinkingThunk = createAsyncThunk(
  'ssoConfig/pollSSOLinking',
  async (paidolId: string, { dispatch, signal }) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_POLL_DURATION_MS = 20 * 60 * 1000; // 20 min hard cap
    // Bail after this many consecutive fetch failures so a transient AppSync
    // 5xx, network blip, or token-refresh mid-poll doesn't leave the wizard
    // showing a frozen spinner indefinitely. 5 × 3s = ~15s of tolerance for
    // ephemeral errors, which covers normal hiccups without burning poll
    // budget on a permanent failure (revoked token, schema mismatch).
    const MAX_CONSECUTIVE_ERRORS = 5;
    const start = Date.now();
    let consecutiveErrors = 0;
    while (!signal.aborted && Date.now() - start < MAX_POLL_DURATION_MS) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const config = await userService.getSSOSetup(paidolId);
        consecutiveErrors = 0;
        if (signal.aborted) return;
        if (!config) return;
        dispatch(applyPolledSSOConfiguration(config));
        if (config.linkingStatus !== 'IN_PROGRESS') return;
      } catch (err) {
        if (signal.aborted) return;
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          // eslint-disable-next-line no-console
          console.warn(
            `SSO linking poll giving up after ${MAX_CONSECUTIVE_ERRORS} consecutive errors; refresh the page to resume.`,
            err
          );
          return;
        }
        // Fall through to the delay; next iteration will retry.
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, POLL_INTERVAL_MS);
        signal.addEventListener('abort', () => {
          clearTimeout(timer);
          resolve(undefined);
        });
      });
    }
  }
);

const applyOperationResponse = (state: SSOConfigState, payload: SSOOperationResponse) => {
  state.operationLoading = false;
  if (payload.success && payload.configuration) {
    state.configuration = payload.configuration;
  }
  state.error = payload.error ?? null;
};

const ssoConfigSlice = createSlice({
  name: 'ssoConfig',
  initialState,
  reducers: {
    clearSSOConfig: () => initialState,
    clearSSOError: (state) => {
      state.error = null;
    },
    clearMetadataValidation: (state) => {
      state.metadataValidation = null;
    },
    /** Background-refresh of the configuration row from the polling thunk.
     * Intentionally does NOT touch `loading` so SSOStatusView stays mounted
     * across poll cycles — using getSSOSetupThunk here would flash a spinner
     * every 3s and trigger a remount-loop. */
    applyPolledSSOConfiguration: (state, action: PayloadAction<SSOConfiguration>) => {
      state.configuration = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Reset SSO state when the user signs out so a subsequent sign-in from a
    // different admin doesn't see stale configuration from the previous session.
    builder.addCase(signOut.fulfilled, () => initialState);

    builder
      .addCase(getSSOSetupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSSOSetupThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.configuration = action.payload;
      })
      .addCase(getSSOSetupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load SSO configuration';
      });

    builder
      .addCase(initiateSSOSetupThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(initiateSSOSetupThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
      })
      .addCase(initiateSSOSetupThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to initiate SSO setup';
      });

    builder
      .addCase(addSSODomainThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(addSSODomainThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
      })
      .addCase(addSSODomainThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to add domain';
      });

    builder
      .addCase(removeSSODomainThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(removeSSODomainThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
        // Only clear cached DNS diagnostics if the remove actually succeeded.
        // When the backend rejects the remove, the domain is still there and
        // the user would otherwise lose their verification status.
        if (action.payload.success) {
          const removed = action.meta.arg.domain;
          delete state.verificationByDomain[removed];
        }
      })
      .addCase(removeSSODomainThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to remove domain';
      });

    builder
      .addCase(verifySSODomainThunk.pending, (state, action) => {
        state.error = null;
        delete state.verificationByDomain[action.meta.arg.domain];
      })
      .addCase(verifySSODomainThunk.fulfilled, (state, action) => {
        const { domain, response } = action.payload;
        state.verificationByDomain[domain] = response;
        if (response.verified && state.configuration) {
          state.configuration.domains = state.configuration.domains.map((d) =>
            d.domain === domain ? { ...d, verified: true } : d
          );
        }
      })
      .addCase(verifySSODomainThunk.rejected, (state, action) => {
        state.error = action.error.message ?? 'Verification failed';
      });

    builder
      .addCase(validateSSOMetadataThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.metadataValidation = null;
      })
      .addCase(validateSSOMetadataThunk.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.metadataValidation = action.payload;
        // Validation failures render via the inline alert in step 4 that reads
        // metadataValidation.error directly; don't also set state.error or the
        // user sees the same message twice.
      })
      .addCase(validateSSOMetadataThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to validate metadata URL';
      });

    builder
      .addCase(saveSSOProviderThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(saveSSOProviderThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
      })
      .addCase(saveSSOProviderThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to save provider';
      });

    builder
      .addCase(activateSSOThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(activateSSOThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
        state.lastNewlyLinkedUserCount = action.payload.newlyLinkedUserCount ?? null;
        state.lastAlreadyLinkedUserCount = action.payload.alreadyLinkedUserCount ?? null;
      })
      .addCase(activateSSOThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Activation failed';
      });

    builder
      .addCase(deactivateSSOThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(deactivateSSOThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
      })
      .addCase(deactivateSSOThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Deactivation failed';
      });

    builder
      .addCase(relinkSSOUsersThunk.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(relinkSSOUsersThunk.fulfilled, (state, action) => {
        applyOperationResponse(state, action.payload);
      })
      .addCase(relinkSSOUsersThunk.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.error.message ?? 'Failed to start relink job';
      });
  },
});

export const { clearSSOConfig, clearSSOError, clearMetadataValidation, applyPolledSSOConfiguration } =
  ssoConfigSlice.actions;

export const selectSSOConfig = (state: RootState) => state.ssoConfig;

export default ssoConfigSlice.reducer;
